import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table, Typography } from 'antd';
import moment from 'moment';

import CustomModal from '../../common/modals/CustomModal';
import CustomModalRedesigned from '../../common/modals/CustomModalRedesigned';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { safeExtractFromObject } from '../../helpers/helpers';
import colors from '../../constants/Colors';
import {
  HEADER_CELL_STYLE,
  SCOPE_LABELS,
  REASON_LABELS,
  formatPercent,
  blockTitle,
} from './emailBlockListHelpers';

const { Text } = Typography;

function EmailBlockListModal({
  visible,
  onClose,
  blocks,
  onUnblock,
}) {
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const hasRedesign = settings?.uxRedesignPt1;

  const onHeaderRow = useCallback(() => {
    if (!hasRedesign) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [hasRedesign]);

  const columns = useMemo(() => [
    {
      title: 'Block',
      key: 'block',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => <Text strong>{blockTitle(block)}</Text>,
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 96,
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => SCOPE_LABELS[block.scope] || block.scope,
    },
    {
      title: 'Reason',
      key: 'reason',
      width: 128,
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => REASON_LABELS[block.reason] || block.reason,
    },
    {
      title: 'Complaint Rate',
      key: 'complaintRate',
      width: 112,
      align: 'right',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => formatPercent(block.complaintRate) || <Text type="secondary">-</Text>,
    },
    {
      title: 'Bounce Rate',
      key: 'bounceRate',
      width: 104,
      align: 'right',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => formatPercent(block.bounceRate) || <Text type="secondary">-</Text>,
    },
    {
      title: 'Emails Sent',
      key: 'sentCount',
      width: 96,
      align: 'right',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => (
        block.sentCount
          ? block.sentCount.toLocaleString()
          : <Text type="secondary">-</Text>
      ),
    },
    {
      title: 'Blocked On',
      key: 'createdAt',
      width: 112,
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => (
        block.createdAt
          ? moment(block.createdAt).format('MMM D, YYYY')
          : <Text type="secondary">-</Text>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 96,
      fixed: 'right',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => (
        <OnTraccrButton
          title="Unblock"
          onClick={() => onUnblock(block.id)}
        />
      ),
    },
  ], [onUnblock]);

  const table = (
    <Table
      columns={columns}
      dataSource={blocks}
      rowKey="id"
      size="small"
      pagination={false}
      onHeaderRow={onHeaderRow}
      scroll={{ x: 840 }}
      style={{ width: '100%' }}
    />
  );

  if (hasRedesign) {
    return (
      <CustomModalRedesigned
        title="Email sending blocks"
        visible={visible}
        onCloseClicked={onClose}
        width={920}
        maskColor={colors.MODAL_MASK_DARK}
        destroyOnClose
      >
        {table}
      </CustomModalRedesigned>
    );
  }

  return (
    <CustomModal
      title="Email Block List"
      visible={visible}
      onCloseClicked={onClose}
      width={920}
      maskColor={colors.MODAL_MASK_DARK}
      destroyOnClose
    >
      {table}
    </CustomModal>
  );
}

EmailBlockListModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  blocks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    scope: PropTypes.string,
    reason: PropTypes.string,
    templateName: PropTypes.string,
    emailType: PropTypes.string,
    complaintRate: PropTypes.number,
    bounceRate: PropTypes.number,
    sentCount: PropTypes.number,
    createdAt: PropTypes.number,
  })).isRequired,
  onUnblock: PropTypes.func.isRequired,
};

export default EmailBlockListModal;
